import { useEffect, useRef, useState } from 'react';
import { db } from '../../services/firebase';
import { doc, setDoc, onSnapshot, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { PhoneOff, Video, VideoOff, Mic, MicOff, FlipHorizontal } from 'lucide-react';

interface Props {
  currentUser: any;
  remoteUser: { uid: string; displayName: string; photoURL?: string };
  callType: 'audio' | 'video';
  isIncoming?: boolean;
  callId?: string;
  chatId?: string;
  onEnd: () => void;
  onMinimize?: () => void;
  minimized?: boolean;
}

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ],
};

const getInitials = (name: string) => (name || 'U').slice(0, 2).toUpperCase();

export const VideoCall = ({ currentUser, remoteUser, callType, isIncoming = false, callId: incomingCallId, chatId, onEnd, onMinimize, minimized = false }: Props) => {
  const [status, setStatus] = useState<'calling' | 'connected' | 'ended'>('calling');
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(callType === 'video');
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [pipExpanded, setPipExpanded] = useState(false);
  const [pipPos, setPipPos] = useState({ x: 16, y: 16 }); // bottom-right offset
  const pipDragging = useRef(false);
  const pipDragStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });
  const [callDuration, setCallDuration] = useState(0);
  const callStartTimeRef = useRef<Date | null>(null);

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const callDocId = useRef<string>(
    incomingCallId || [currentUser.uid, remoteUser.uid].sort().join('_call_')
  );
  const durationInterval = useRef<any>(null);
  const unsubsRef = useRef<(() => void)[]>([]);
  // Queue ICE candidates that arrive before remoteDescription is set
  const pendingCandidatesRef = useRef<RTCIceCandidateInit[]>([]);
  const remoteDescSetRef = useRef(false);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const audioTrackRef = useRef<MediaStreamTrack | null>(null);

  useEffect(() => {
    if (!isIncoming) {
      startCall();
    } else {
      // El CallManager ya manejó el ringtone y la UI de "llamada entrante"
      // Aquí solo conectamos el WebRTC directamente
      answerCall();
    }
    return () => cleanup();
  }, []);

  // ── Ringtone lo maneja CallManager ──

  useEffect(() => {
    if (status === 'connected') {
      callStartTimeRef.current = new Date();
      durationInterval.current = setInterval(() => setCallDuration(d => d + 1), 1000);
      // Re-assign streams in case refs were not mounted when ontrack fired
      if (remoteVideoRef.current && remoteStreamRef.current) {
        remoteVideoRef.current.srcObject = remoteStreamRef.current;
      }
      if (localVideoRef.current && localStreamRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }
    }
    return () => clearInterval(durationInterval.current);
  }, [status]);

  // Stable ref callbacks - only assign srcObject when the element mounts
  const setRemoteVideoRef = (el: HTMLVideoElement | HTMLAudioElement | null) => {
    (remoteVideoRef as any).current = el;
    if (el && remoteStreamRef.current && (el as any).srcObject !== remoteStreamRef.current) {
      (el as any).srcObject = remoteStreamRef.current;
    }
  };

  const setLocalVideoRef = (el: HTMLVideoElement | null) => {
    (localVideoRef as any).current = el;
    if (el && localStreamRef.current && el.srcObject !== localStreamRef.current) {
      el.srcObject = localStreamRef.current;
    }
  };

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const getMedia = async (facing: 'user' | 'environment' = 'user') => {
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
      video: callType === 'video' ? {
        facingMode: facing,
        width: { ideal: 1280 },
        height: { ideal: 720 },
      } : false,
    });
    // Save audio track in its own ref so it survives camera switches
    const audioTrack = stream.getAudioTracks()[0];
    if (audioTrack) audioTrackRef.current = audioTrack;
    localStreamRef.current = stream;
    if (localVideoRef.current) localVideoRef.current.srcObject = stream;
    return stream;
  };

  const createPeerConnection = (stream: MediaStream) => {
    pcRef.current?.close();
    const pc = new RTCPeerConnection(ICE_SERVERS);
    pcRef.current = pc;
    stream.getTracks().forEach(track => pc.addTrack(track, stream));
    pc.ontrack = (e) => {
      remoteStreamRef.current = e.streams[0];
      if (remoteVideoRef.current) (remoteVideoRef.current as any).srcObject = e.streams[0];
      setStatus('connected');
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        endCall(true);
      }
    };
    return pc;
  };

  const addPendingCandidates = async (pc: RTCPeerConnection) => {
    for (const candidate of pendingCandidatesRef.current) {
      try {
        if (pc.signalingState !== 'closed') {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        }
      } catch {}
    }
    pendingCandidatesRef.current = [];
  };

  const safeAddCandidate = async (pc: RTCPeerConnection, candidate: RTCIceCandidateInit) => {
    if (!remoteDescSetRef.current) {
      pendingCandidatesRef.current.push(candidate);
      return;
    }
    try {
      if (pc.signalingState !== 'closed') {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      }
    } catch {}
  };

  // ── CALLER ──
  const startCall = async () => {
    try {
      const stream = await getMedia();
      const pc = createPeerConnection(stream);
      const callRef = doc(db, 'calls', callDocId.current);
      remoteDescSetRef.current = false;
      pendingCandidatesRef.current = [];

      pc.onicecandidate = async (e) => {
        if (e.candidate) {
          await addDoc(collection(db, 'calls', callDocId.current, 'callerCandidates'), e.candidate.toJSON());
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      await setDoc(callRef, {
        callerId: currentUser.uid,
        calleeId: remoteUser.uid,
        callerName: currentUser.displayName || 'Usuario',
        callerPhoto: currentUser.photoURL || null,
        calleeName: remoteUser.displayName,
        callType,
        offer: { type: offer.type, sdp: offer.sdp },
        status: 'calling',
        createdAt: serverTimestamp(),
      });

      // Listen for answer
      const unsub1 = onSnapshot(callRef, async (snap) => {
        const data = snap.data();
        if (!data) return;
        if (data.answer && !remoteDescSetRef.current) {
          remoteDescSetRef.current = true;
          await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
          await addPendingCandidates(pc);
        }
        if (data.status === 'rejected' || data.status === 'ended') {
          endCall(false);
        }
      });
      unsubsRef.current.push(unsub1);

      // Listen for callee ICE candidates
      const unsub2 = onSnapshot(collection(db, 'calls', callDocId.current, 'calleeCandidates'), (snap) => {
        snap.docChanges().forEach(change => {
          if (change.type === 'added') safeAddCandidate(pc, change.doc.data() as RTCIceCandidateInit);
        });
      });
      unsubsRef.current.push(unsub2);

    } catch (e) {
      console.error('Error starting call:', e);
      endCall(false);
    }
  };

  // ── CALLEE ──
  const answerCall = async () => {
    try {
      const stream = await getMedia();
      const pc = createPeerConnection(stream);
      const callRef = doc(db, 'calls', callDocId.current);
      remoteDescSetRef.current = false;
      pendingCandidatesRef.current = [];

      pc.onicecandidate = async (e) => {
        if (e.candidate) {
          await addDoc(collection(db, 'calls', callDocId.current, 'calleeCandidates'), e.candidate.toJSON());
        }
      };

      // Get offer from Firestore
      const callSnap = await new Promise<any>((resolve) => {
        const unsub = onSnapshot(callRef, (s) => {
          const data = s.data();
          if (data?.offer) { resolve(data); unsub(); }
        });
      });

      await pc.setRemoteDescription(new RTCSessionDescription(callSnap.offer));
      remoteDescSetRef.current = true;

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      await setDoc(callRef, {
        answer: { type: answer.type, sdp: answer.sdp },
        status: 'accepted',
      }, { merge: true });

      await addPendingCandidates(pc);

      // Listen for caller ICE candidates
      const unsub = onSnapshot(collection(db, 'calls', callDocId.current, 'callerCandidates'), (snap) => {
        snap.docChanges().forEach(change => {
          if (change.type === 'added') safeAddCandidate(pc, change.doc.data() as RTCIceCandidateInit);
        });
      });
      unsubsRef.current.push(unsub);

      setStatus('connected');
    } catch (e) {
      console.error('Error answering call:', e);
      endCall(false);
    }
  };

  const endCall = async (notify = true) => {
    if (notify) {
      try {
        await setDoc(doc(db, 'calls', callDocId.current), { status: 'ended' }, { merge: true });
      } catch {}
    }
    // Save call log as a message in the chat
    try {
      const duration = callStartTimeRef.current
        ? Math.floor((new Date().getTime() - callStartTimeRef.current.getTime()) / 1000)
        : 0;
      const targetChatId = chatId || [currentUser.uid, remoteUser.uid].sort().join('_');
      // Find the chat document
      // Save as message
      await addDoc(collection(db, 'chats', targetChatId, 'messages'), {
        type: 'call',
        callType,
        duration,
        senderId: currentUser.uid,
        status: duration > 0 ? 'completed' : 'missed',
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.warn('Could not save call log:', e);
    }
    cleanup();
    setStatus('ended');
    setTimeout(() => onEnd(), 600);
  };

  const cleanup = () => {
    unsubsRef.current.forEach(u => u());
    unsubsRef.current = [];
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    audioTrackRef.current?.stop();
    audioTrackRef.current = null;
    localStreamRef.current = null;
    if (pcRef.current) {
      pcRef.current.ontrack = null;
      pcRef.current.onicecandidate = null;
      pcRef.current.close();
      pcRef.current = null;
    }
    clearInterval(durationInterval.current);
  };

  // ── PIP drag handlers ──
  const onPipPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    pipDragging.current = true;
    pipDragStart.current = { mx: e.clientX, my: e.clientY, px: pipPos.x, py: pipPos.y };
    window.addEventListener('pointermove', onPipPointerMove);
    window.addEventListener('pointerup', onPipPointerUp);
  };

  const onPipPointerMove = (e: PointerEvent) => {
    if (!pipDragging.current) return;
    const dx = e.clientX - pipDragStart.current.mx;
    const dy = e.clientY - pipDragStart.current.my;
    setPipPos({ x: pipDragStart.current.px - dx, y: pipDragStart.current.py - dy });
  };

  const onPipPointerUp = () => {
    pipDragging.current = false;
    window.removeEventListener('pointermove', onPipPointerMove);
    window.removeEventListener('pointerup', onPipPointerUp);
  };

  const toggleCamera = async () => {
    const newFacing = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacing);
    try {
      // Stop only the current video track — NEVER touch audio
      localStreamRef.current?.getVideoTracks().forEach(t => t.stop());

      // Request only video — audio: false preserves the mic completely
      const newVideoStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacing, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      const newVideoTrack = newVideoStream.getVideoTracks()[0];

      // Replace video sender in peer connection only
      if (newVideoTrack && pcRef.current) {
        const videoSender = pcRef.current.getSenders().find(s => s.track?.kind === 'video');
        if (videoSender) await videoSender.replaceTrack(newVideoTrack);
      }

      // Rebuild local stream: use the saved audio track ref + new video
      const tracks: MediaStreamTrack[] = [];
      if (audioTrackRef.current && audioTrackRef.current.readyState === 'live') {
        tracks.push(audioTrackRef.current);
      }
      tracks.push(newVideoTrack);

      const combinedStream = new MediaStream(tracks);
      localStreamRef.current = combinedStream;
      if (localVideoRef.current) localVideoRef.current.srcObject = combinedStream;
    } catch (e) {
      console.error('Error switching camera:', e);
    }
  };

  const toggleMic = () => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (track) { track.enabled = !track.enabled; setMicOn(t => !t); }
  };

  const toggleCam = () => {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (track) { track.enabled = !track.enabled; setCamOn(t => !t); }
  };

  // ── Minimized floating bubble ──
  if (minimized && status === 'connected') {
    return (
      <div style={{
        position: 'fixed', bottom: '90px', right: '16px', zIndex: 5000,
        background: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
        borderRadius: '20px', padding: '0.6rem 1rem',
        display: 'flex', alignItems: 'center', gap: '0.6rem',
        boxShadow: '0 4px 20px rgba(124,58,237,0.5)',
        cursor: 'pointer', minWidth: '160px',
      }} onClick={() => onMinimize && onMinimize()}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%', overflow: 'hidden',
          border: '2px solid rgba(255,255,255,0.3)', flexShrink: 0,
        }}>
          {remoteUser.photoURL
            ? <img src={remoteUser.photoURL} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" />
            : <div style={{ width: '100%', height: '100%', background: '#4c1d95', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>{getInitials(remoteUser.displayName)}</div>}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: '0.82rem' }}>{remoteUser.displayName}</div>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem' }}>
            {callType === 'video' ? '📹' : '📞'} {formatDuration(callDuration)}
          </div>
        </div>
        <button onClick={(e) => { e.stopPropagation(); endCall(true); }} style={{
          background: '#ef4444', border: 'none', borderRadius: '50%',
          width: 32, height: 32, display: 'flex', alignItems: 'center',
          justifyContent: 'center', cursor: 'pointer', color: '#fff', flexShrink: 0,
        }}>
          <PhoneOff size={14} />
        </button>
      </div>
    );
  }

  // ── Connecting screen (receptor mientras answerCall() corre) ──
  if (isIncoming && status === 'calling') {
    return (
      <div className="call-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0f' }}>
        <div style={{ textAlign: 'center', color: '#fff' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>
            {callType === 'video' ? '📹' : '📞'}
          </div>
          <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{remoteUser.displayName}</div>
          <div style={{ fontSize: '0.85rem', opacity: 0.6, marginTop: '0.5rem' }}>Conectando...</div>
        </div>
      </div>
    );
  }

  // ── Active call UI ──
  return (
    <div className="call-overlay" style={{ display: 'flex', flexDirection: 'column', background: '#0a0a0f' }}>

      {/* ── Top bar: name + minimize ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '1rem 1.25rem', flexShrink: 0,
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)',
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
      }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>{remoteUser.displayName}</div>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
            {status === 'calling' && (callType === 'video' ? '📹 Llamando...' : '📞 Llamando...')}
            {status === 'connected' && formatDuration(callDuration)}
            {status === 'ended' && 'Llamada terminada'}
          </div>
        </div>
        {onMinimize && status === 'connected' && (
          <button onClick={onMinimize} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '20px',
            color: '#fff', padding: '0.4rem 0.9rem', cursor: 'pointer', fontSize: '0.8rem',
            backdropFilter: 'blur(8px)',
          }}>
            ↙ Minimizar
          </button>
        )}
      </div>

      {/* ── Video area ── */}
      {/* Elemento audio oculto — siempre presente para reproducir el audio remoto */}
      <audio ref={setRemoteVideoRef} autoPlay playsInline style={{ display: 'none' }} />

      {callType === 'video' ? (
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <video ref={setRemoteVideoRef} autoPlay playsInline style={{
            width: '100%', height: '100%', objectFit: 'cover', background: '#111',
          }} />
          {/* Draggable PIP */}
          <video
            ref={setLocalVideoRef}
            autoPlay playsInline muted
            onPointerDown={onPipPointerDown}
            onClick={() => !pipDragging.current && setPipExpanded(p => !p)}
            style={{
              position: 'absolute',
              bottom: `${pipPos.y}px`,
              right: `${pipPos.x}px`,
              width: pipExpanded ? '45%' : '28%',
              aspectRatio: '9/16',
              objectFit: 'cover',
              borderRadius: '14px',
              border: '2px solid #7c3aed',
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
              transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
              transition: pipDragging.current ? 'none' : 'width 0.3s ease, aspect-ratio 0.3s ease',
              cursor: 'grab',
              zIndex: 5,
              touchAction: 'none',
            }}
          />
        </div>
      ) : (
        /* Audio call center */
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
          <div style={{ width: 100, height: 100, borderRadius: '50%', overflow: 'hidden', border: '3px solid #7c3aed' }}>
            {remoteUser.photoURL
              ? <img src={remoteUser.photoURL} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" />
              : <div style={{ width: '100%', height: '100%', background: '#4c1d95', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '2rem', fontWeight: 700 }}>{getInitials(remoteUser.displayName)}</div>}
          </div>
        </div>
      )}

      {/* ── Bottom controls ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem',
        padding: '1.5rem 1rem calc(1.5rem + env(safe-area-inset-bottom, 0px))',
        background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
        flexShrink: 0,
      }}>
        <button className={`call-ctrl-btn ${!micOn ? 'call-ctrl-btn--off' : ''}`} onClick={toggleMic}>
          {micOn ? <Mic size={20} /> : <MicOff size={20} />}
        </button>
        {callType === 'video' && (
          <button className={`call-ctrl-btn ${!camOn ? 'call-ctrl-btn--off' : ''}`} onClick={toggleCam}>
            {camOn ? <Video size={20} /> : <VideoOff size={20} />}
          </button>
        )}
        {callType === 'video' && (
          <button className="call-ctrl-btn" onClick={toggleCamera}>
            <FlipHorizontal size={20} />
          </button>
        )}
        <button className="call-ctrl-btn call-ctrl-btn--end" onClick={() => endCall(true)}>
          <PhoneOff size={20} />
        </button>
      </div>
    </div>
  );
};